import { CatalogPager as LinearPager, Currency } from '../components/ui/DataPrimitives';
import { formatNumber as formatCount } from '../utils/numberFormat';
import { PageShell, TableShell } from '../components/ui/DataPrimitives';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { API_BASE } from '../config';
import { getGradeColor } from '../components/performance/gradeColors';
import './ListLinear.css';

function Suppliers() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [search, setSearch] = useState('');

  // Taxonomy cascading filters
  const [level1Filter, setLevel1Filter] = useState('');
  const [level2Filter, setLevel2Filter] = useState('');
  const [level3Filter, setLevel3Filter] = useState('');
  const [level4Filter, setLevel4Filter] = useState('');
  const [level1Options, setLevel1Options] = useState([]);
  const [level2Options, setLevel2Options] = useState([]);
  const [level3Options, setLevel3Options] = useState([]);
  const [level4Options, setLevel4Options] = useState([]);

  // Sorting (client-side via column headers)
  const [sortColumn, setSortColumn] = useState('spend_12mo');
  const [sortDirection, setSortDirection] = useState('desc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const suppliersTableRef = useRef(null);

  // Stats
  const [stats, setStats] = useState({
    totalSuppliers: 0,
    activeSuppliers: 0,
    totalSpend: 0
  });

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  // Single taxonomy fetcher — accepts filter params, returns all level values
  const fetchTaxonomyLevels = async (params = {}) => {
    try {
      const qs = new URLSearchParams(params).toString();
      const url = qs
        ? `${API_BASE}/api/taxonomy/levels?${qs}`
        : `${API_BASE}/api/taxonomy/levels`;
      const response = await fetch(url, { headers: getAuthHeaders() });
      if (response.ok) return await response.json();
    } catch (error) {
      console.error('Error fetching taxonomy levels:', error);
    }
    return {};
  };

  // Lane 1385: each option shows the shared category name and sends the raw value.
  const taxonomyOptions = (data, n) => (data[`level_${n}_values`] || []).map((v) => (
    { value: v, label: (data[`level_${n}_labels`] || {})[v] || v }));

  useEffect(() => {
    fetchSuppliers();
    // Fetch L1 taxonomy options
    fetchTaxonomyLevels().then(data => {
      setLevel1Options(taxonomyOptions(data, 1));
    });
  }, [token]);

  // Cascade: when L1 changes, fetch L2 options and clear L2-L4
  useEffect(() => {
    if (level1Filter) {
      fetchTaxonomyLevels({ level_1: level1Filter }).then(data => {
        setLevel2Options(taxonomyOptions(data, 2));
      });
    } else {
      setLevel2Options([]);
    }
    setLevel2Filter('');
    setLevel3Filter('');
    setLevel4Filter('');
    setLevel3Options([]);
    setLevel4Options([]);
  }, [level1Filter]);

  // Cascade: when L2 changes, fetch L3 options and clear L3-L4
  useEffect(() => {
    if (level1Filter && level2Filter) {
      fetchTaxonomyLevels({ level_1: level1Filter, level_2: level2Filter }).then(data => {
        setLevel3Options(taxonomyOptions(data, 3));
      });
    } else {
      setLevel3Options([]);
    }
    setLevel3Filter('');
    setLevel4Filter('');
    setLevel4Options([]);
  }, [level2Filter]);

  // Cascade: when L3 changes, fetch L4 options and clear L4
  useEffect(() => {
    if (level1Filter && level2Filter && level3Filter) {
      fetchTaxonomyLevels({ level_1: level1Filter, level_2: level2Filter, level_3: level3Filter }).then(data => {
        setLevel4Options(taxonomyOptions(data, 4));
      });
    } else {
      setLevel4Options([]);
    }
    setLevel4Filter('');
  }, [level3Filter]);

  const fetchSuppliers = async () => {
    if (!token) return;

    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('per_page', pageSize);
      if (search) params.append('search', search);
      if (level1Filter) params.append('level_1', level1Filter);
      if (level2Filter) params.append('level_2', level2Filter);
      if (level3Filter) params.append('level_3', level3Filter);
      if (level4Filter) params.append('level_4', level4Filter);

      const response = await fetch(`${API_BASE}/api/supplier-hub/list?${params}`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setSuppliers(data.suppliers || []);

        // Use server-provided aggregate stats (computed across all matching suppliers, not just current page)
        const totalFromServer = data.total_count != null ? data.total_count : (data.suppliers || []).length;
        setStats({
          totalSuppliers: totalFromServer,
          activeSuppliers: data.active_count != null ? data.active_count : data.suppliers.filter(s => s.status === 'active').length,
          totalSpend: data.total_spend != null ? data.total_spend : data.suppliers.reduce((sum, s) => sum + (s.spend_12mo || 0), 0)
        });
      } else {
        setError('Failed to load suppliers');
      }
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      setError('Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch when search, filters, or page change (backend handles filtering + pagination)
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchSuppliers();
    }, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [currentPage, pageSize, search, level1Filter, level2Filter, level3Filter, level4Filter, token]);

  const handleSort = (columnKey) => {
    if (sortColumn === columnKey) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnKey);
      // Default desc for numeric columns, asc for text (Grade is a letter -> asc,
      // so A sorts to the top).
      const numericCols = ['spend_12mo', 'material_count', 'active_contract_count'];
      setSortDirection(numericCols.includes(columnKey) ? 'desc' : 'asc');
    }
  };

  const hasActiveFilters = search || level1Filter || level2Filter || level3Filter || level4Filter;

  const clearFilters = () => {
    setSearch(''); setLevel1Filter(''); setLevel2Filter(''); setLevel3Filter(''); setLevel4Filter('');
  };

  // Sort suppliers (filtering is handled server-side via /api/supplier-hub/list params)
  const displaySuppliers = useMemo(() => {
    return [...suppliers].sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];

      // Handle nulls — push to end
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // Numeric columns
      if (['spend_12mo', 'total_spend', 'material_count', 'primary_material_count',
           'active_contract_count', 'expired_contract_count', 'po_count'].includes(sortColumn)) {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }
      // Array columns (categories) — sort by first element or length
      else if (sortColumn === 'categories') {
        aVal = (aVal && aVal.length) ? aVal[0].toLowerCase() : '';
        bVal = (bVal && bVal.length) ? bVal[0].toLowerCase() : '';
      }
      // String columns
      else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [suppliers, sortColumn, sortDirection]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, level1Filter, level2Filter, level3Filter, level4Filter]);

  // Spend shown in $M to 2 decimals per Rev-1 (e.g. $3.68M); count in tabular numerals.

  // Honest page-local-sort flag (parity note): the full set is backend-ordered by
  // spend; a column sort reorders only the current page.
  const SORT_LABELS = {
    supplier_name: 'Supplier', categories: 'Categories', spend_12mo: 'spend',
    material_count: 'Materials', scorecard_grade: 'Grade', active_contract_count: 'Contracts'
  };
  const sortIndicator = (sortColumn === 'spend_12mo' && sortDirection === 'desc')
    ? 'Sorted by spend'
    : `Sorted by ${SORT_LABELS[sortColumn] || 'spend'} · this page`;

  const openSupplier = (row) => navigate(`/suppliers/${encodeURIComponent(row.supplier_name)}`);

  const sortHeader = (key, label, { right = false, hideSm = false } = {}) => {
    const cls = ['ll-sort'];
    if (right) cls.push('ll-hr');
    if (hideSm) cls.push('ll-hide-sm');
    if (sortColumn === key) cls.push(sortDirection === 'asc' ? 'll-asc' : 'll-desc');
    return (
      <span
        className={cls.join(' ')}
        role="button"
        tabIndex={0}
        onClick={() => handleSort(key)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSort(key); } }}
      >{label}</span>
    );
  };

  if (loading && suppliers.length === 0 && !error) {
    return (
      <PageShell className="ll">
        <div className="ll-wrap">
          <div className="ll-loading">Loading suppliers…</div>
        </div>
      </PageShell>
    );
  }

  if (error && suppliers.length === 0) {
    return (
      <PageShell className="ll">
        <div className="ll-wrap">
          <header className="ll-head"><div><h1 className="ll-h1">Supplier Hub</h1></div></header>
          <div className="ll-empty">
            <div className="ll-empty-t">Unable to load suppliers</div>
            Please refresh the page. <button className="ll-clr" onClick={fetchSuppliers} style={{ color: 'var(--ll-accent)' }}>Retry</button>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell className="ll">
      <div className="ll-wrap">
        <header className="ll-head">
          <div>
            <h1 className="ll-h1">Supplier Hub</h1>
            <div className="ll-sub">Every supplier in your network. Find one, or scan a slice, on the way to its dossier</div>
          </div>
        </header>

        {/* portfolio figure strip (surfaces the fetched-but-unshown active_count) */}
        <section className="ll-figs" aria-label="Portfolio summary">
          <div className="ll-fig">
            <div className="ll-fig-k">Suppliers</div>
            <div className="ll-fig-v">{formatCount(stats.totalSuppliers)}</div>
            <div className="ll-fig-d">in the system</div>
          </div>
          <div className="ll-fig">
            <div className="ll-fig-k">Active</div>
            <div className="ll-fig-v">{formatCount(stats.activeSuppliers)}</div>
            <div className="ll-fig-d">bought from · last 12 mo</div>
          </div>
          <div className="ll-fig">
            <div className="ll-fig-k">Spend · 12 mo</div>
            <div className="ll-fig-v"><Currency value={stats.totalSpend}/></div>
            <div className="ll-fig-d">across the filtered set</div>
          </div>
        </section>

        {/* filter bar: search + 4 cascading taxonomy levels */}
        <div className="ll-fbar">
          <div className="ll-search">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></svg>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search suppliers" aria-label="Search suppliers" />
          </div>
          <select className="ll-sel" value={level1Filter} onChange={(e) => setLevel1Filter(e.target.value)} aria-label="Category (level 1)">
            <option value="">Category: all</option>
            {level1Options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select className="ll-sel" value={level2Filter} onChange={(e) => setLevel2Filter(e.target.value)} disabled={!level1Filter} aria-label="Subcategory (level 2)">
            <option value="">{level1Filter ? 'Subcategory: all' : 'Subcategory: pick a category'}</option>
            {level2Options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select className="ll-sel" value={level3Filter} onChange={(e) => setLevel3Filter(e.target.value)} disabled={!level2Filter} aria-label="Type (level 3)">
            <option value="">{level2Filter ? 'Type: all' : 'Type: pick a subcategory'}</option>
            {level3Options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select className="ll-sel" value={level4Filter} onChange={(e) => setLevel4Filter(e.target.value)} disabled={!level3Filter} aria-label="Detail (level 4)">
            <option value="">{level3Filter ? 'Detail: all' : 'Detail: pick a type'}</option>
            {level4Options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {hasActiveFilters && <button className="ll-clr" onClick={clearFilters}>Clear</button>}
          <span className="ll-rt" title="Column sort reorders the current page; the full set is ordered by spend.">{sortIndicator}</span>
        </div>

        {/* filtered results count (matches prior behavior: only shown while a taxonomy filter is active) */}
        {level1Filter && (
          <div className="ll-count">Showing {displaySuppliers.length} of {suppliers.length} suppliers</div>
        )}
        {loading && suppliers.length > 0 && <div className="ll-updating">Updating results…</div>}

        <div ref={suppliersTableRef}>
          <TableShell className="ll-tbl ll-tbl--sup" aria-label="Suppliers">
            {!loading && suppliers.length === 0 ? (
              <div className="ll-empty">{search ? 'No suppliers match your search' : 'No suppliers in your network yet'}</div>
            ) : (
              <>
                <div className="ll-row ll-hd">
                  {sortHeader('supplier_name', 'Supplier')}
                  {sortHeader('categories', 'Categories', { hideSm: true })}
                  {sortHeader('spend_12mo', 'Spend · 12 mo', { right: true })}
                  {sortHeader('material_count', 'Materials', { right: true, hideSm: true })}
                  {sortHeader('scorecard_grade', 'Grade', { right: true })}
                  {sortHeader('active_contract_count', 'Contracts', { hideSm: true })}
                </div>
                {displaySuppliers.map((s) => {
                  const cats = s.categories || [];
                  const catText = cats.slice(0, 2).join(', ');
                  // QA R2-B: the scorecard letter grade, color-coded via the shared
                  // dossier grade palette; null (-> "—") when the tenant's scorecard
                  // UI is off or the supplier has no scorecard on file.
                  const gc = s.scorecard_grade ? getGradeColor(s.scorecard_grade) : null;
                  return (
                    <div
                      key={s.supplier_name}
                      className="ll-row"
                      tabIndex={0}
                      onClick={() => openSupplier(s)}
                      onKeyDown={(e) => { if (e.key === 'Enter') openSupplier(s); }}
                    >
                      <div className="ll-name" title={s.supplier_name}>{s.supplier_name}</div>
                      <div className="ll-cats ll-hide-sm" title={cats.join(', ')}>
                        {catText || '-'}{cats.length > 2 && <span className="ll-more"> +{cats.length - 2}</span>}
                      </div>
                      <div className={`ll-num ll-mono${s.spend_12mo ? '' : ' ll-mut'}`}><Currency value={s.spend_12mo}/></div>
                      <div className="ll-num ll-mut ll-mono ll-hide-sm">{formatCount(s.material_count)}</div>
                      <div className="ll-grade">
                        {gc
                          ? <span className="ll-grade-pill" style={{ color: gc.fg, background: gc.bg }}>{s.scorecard_grade}</span>
                          : <span className="ll-grade-na">Not graded</span>}
                      </div>
                      {/* QA R2-B finding 4: expired-aware -- show "N expired" when
                          only lapsed contracts remain, instead of "None". */}
                      <div className={`ll-ct ll-hide-sm${(s.active_contract_count > 0 || s.expired_contract_count > 0) ? '' : ' ll-none'}`}>
                        {s.active_contract_count > 0
                          ? <span className="ll-ctn">{s.active_contract_count}</span>
                          : s.expired_contract_count > 0
                            ? <span className="ll-ctexp">{s.expired_contract_count} expired</span>
                            : 'None'}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </TableShell>
        </div>

        <LinearPager
          totalItems={stats.totalSuppliers}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          scrollTargetRef={suppliersTableRef}
          noun="suppliers"
        />

        <div className="ll-foot">
          <b>Grade</b> is the supplier’s latest quarterly-scorecard letter grade (worst across scopes), color-coded; “Not graded” when no scorecard is on file. <b>Contracts</b> shows the active count, or “N expired” when only lapsed contracts remain. <b>Spend</b> is the trailing-12-month PO total, in millions. Divisions of one parent (e.g. Parker Hannifin - Ord vs. - Css) list as separate rows. The group rollup lives on the dossier, not here.
        </div>
      </div>
    </PageShell>
  );
}

export default Suppliers;
